/**
 * Shared Orange HQ brand mark = OFFICIAL OrangeBySite logo + "HQ" wordmark.
 *
 * NEVER redraw the OrangeBySite wordmark. Always reuse the shared official
 * logo asset (`/orangebysite-logo-white-black-v2.webp`) — the SAME one the
 * public site uses in Nav/Footer/StandaloneHeader.
 *
 * Colour treatments are derived from that ONE official asset using safe CSS
 * filters that only change colour — geometry/design remain identical to the
 * official OrangeBySite logo. No new SVG, no font substitute, no recreation.
 *
 * Locked treatments (share these three across every HQ surface):
 *   dark   → WHITE official logo + ORANGE `HQ`   (black/dark backgrounds)
 *   light  → BLACK official logo + ORANGE `HQ`   (white/light backgrounds)
 *   orange → WHITE official logo + BLACK  `HQ`   (orange hero, e.g. login)
 */
import React from "react";

const LOGO_SRC = "/orangebysite-logo-white-black-v2.webp";

// CSS filters — colour-only. Do NOT change size or aspect ratio.
const LOGO_FILTER = {
  // Force the whole logo to pure white.
  dark:   "brightness(0) invert(1)",
  // Force the whole logo to pure black.
  light:  "brightness(0)",
  // Force the whole logo to pure white (over the orange login hero).
  orange: "brightness(0) invert(1)",
};

const HQ_COLOR = {
  dark:   "text-[#FF6A00]",   // orange HQ on dark
  light:  "text-[#FF6A00]",   // orange HQ on white
  orange: "text-black",       // black HQ on orange
};

const HQ_SIZE = {
  sm: "text-[12px] tracking-[0.28em]",
  md: "text-[16px] tracking-[0.32em]",
  lg: "text-[22px] tracking-[0.32em]",
};
const LOGO_HEIGHT = { sm: "h-7", md: "h-9", lg: "h-12" };

/**
 * @param {"dark"|"light"|"orange"} theme
 * @param {"sm"|"md"|"lg"} size
 */
export default function HQBrand({ theme = "dark", size = "md", className = "", testId = "hq-brand" }) {
  const filter = LOGO_FILTER[theme] || LOGO_FILTER.dark;
  const hqColor = HQ_COLOR[theme] || HQ_COLOR.dark;
  return (
    <span className={`inline-flex items-center gap-2 leading-none ${className}`} data-testid={testId}>
      <img
        src={LOGO_SRC}
        alt="ORANGEBYSITE"
        className={`${LOGO_HEIGHT[size]} w-auto select-none object-contain block`}
        style={{ filter, WebkitFilter: filter }}
        draggable={false}
      />
      <span className={`${HQ_SIZE[size]} font-black uppercase ${hqColor} translate-y-[1px]`} aria-label="HQ">
        HQ
      </span>
    </span>
  );
}
